import React, { useState, useEffect, useRef } from 'react';
import {
  LayoutDashboard,
  Users,
  ShoppingBag,
  CheckCircle,
  Clock,
  LogOut,
  Menu,
  X,
  ChevronDown,
  RefreshCw
} from 'lucide-react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import '../Pages/Admin.css';
import { apiService } from '../api/api';

import UsersPage           from './Admin/UsersPage.jsx';
import ActiveProductsPage  from './Admin/ActiveProductsPage.jsx';
import PendingProductsPage from './Admin/PendingProductsPage.jsx';
import OrdersPage          from './Admin/OrdersPage.jsx';

// Order statuses
const ORDER_STATUSES = {
  1: { label: 'Pending',  color: '#a3d977' },
  2: { label: 'Approved', color: '#2d9e5f' },
  3: { label: 'Rejected', color: '#1a5c38' },
};

const GREEN_PALETTE = {
  bar:        '#52c97d',
  gridStroke: '#e0e0e0',
  axisStroke: '#666',
};

const getOrderStatus = (id) => ORDER_STATUSES[parseInt(id)]?.label || 'Pending';

const isProductActive = (p) => {
  if (p.isActive         !== undefined) return p.isActive         === true || p.isActive         === 1;
  if (p.IsActive         !== undefined) return p.IsActive         === true || p.IsActive         === 1;
  if (p.isVisibleProduct !== undefined) return p.isVisibleProduct === true || p.isVisibleProduct === 1;
  if (p.IsVisibleProduct !== undefined) return p.IsVisibleProduct === true || p.IsVisibleProduct === 1;
  if (p.status           !== undefined) return p.status           === true || p.status           === 1 || p.status === 'active';
  if (p.Status           !== undefined) return p.Status           === true || p.Status           === 1 || p.Status === 'active';
  if (p.isPublished      !== undefined) return p.isPublished      === true || p.isPublished      === 1;
  if (p.IsPublished      !== undefined) return p.IsPublished      === true || p.IsPublished      === 1;
  return true;
};

const VALID_TABS = ['dashboard', 'users', 'orders', 'active-products', 'pending-products'];
const SESSION_KEY = 'adminActiveTab';

const AdminDashboard = ({ onNavigate, onLogout }) => {
  const [activeTab, setActiveTab] = useState(() => {
    const saved = sessionStorage.getItem(SESSION_KEY);
    return saved && VALID_TABS.includes(saved) ? saved : 'dashboard';
  });

  const changeTab = (tab) => {
    sessionStorage.setItem(SESSION_KEY, tab);
    setActiveTab(tab);
  };

  const [isSidebarOpen,         setIsSidebarOpen]         = useState(true);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [adminName,             setAdminName]             = useState('Admin');
  const [dashboardStats,        setDashboardStats]        = useState({
    totalUsers: 0, totalOrders: 0, totalRevenue: 0,
    totalProducts: 0, inactiveProducts: 0, allProducts: 0,
    usersChange: 0, ordersChange: 0, revenueChange: 0, productsChange: 0,
    pendingOrders: 0, approvedOrders: 0, rejectedOrders: 0,
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [chartData,    setChartData]    = useState({
    revenueChart: [], ordersChart: [], orderStatusChart: []
  });
  const [loading,    setLoading]    = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const profileDropdownRef = useRef(null);

  useEffect(() => { fetchDashboardData(); }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // ✅ Step 1: Try localStorage userData first
      const storedUser = JSON.parse(localStorage.getItem('userData') || '{}');
      const userId = localStorage.getItem('userId');
      let storedName = storedUser?.name || storedUser?.Name || storedUser?.email || '';

      // ✅ Step 2: If not in localStorage, fetch from API using userId
      if (!storedName && userId) {
        try {
          const userDetails = await apiService.getUserById(userId);
          storedName = userDetails?.name || userDetails?.Name || userDetails?.email || 'Admin';
        } catch {
          storedName = 'Admin';
        }
      }

      setAdminName(storedName || 'Admin');

      const usersResponse = await apiService.getAllUsers();
      const usersArray =
        Array.isArray(usersResponse)        ? usersResponse :
        Array.isArray(usersResponse?.data)  ? usersResponse.data :
        Array.isArray(usersResponse?.users) ? usersResponse.users : [];

      const userMap = new Map();
      usersArray.forEach(user => {
        const uid = user.id || user.userId || user.ID;
        if (uid) userMap.set(parseInt(uid), user);
      });

      let productsArray = [];
      try {
        const dr = await apiService.getAllProductsForDisplay();
        productsArray =
          Array.isArray(dr)        ? dr :
          Array.isArray(dr?.data)  ? dr.data :
          Array.isArray(dr?.items) ? dr.items : [];
      } catch {
        try {
          const fb = await apiService.getAllProducts();
          productsArray = Array.isArray(fb) ? fb : Array.isArray(fb?.data) ? fb.data : [];
        } catch { }
      }

      const activeProducts   = productsArray.filter(p =>  isProductActive(p));
      const inactiveProducts = productsArray.filter(p => !isProductActive(p));

      let allOrders = [];
      try {
        const or = await apiService.get('/Orders');
        allOrders = Array.isArray(or) ? or : Array.isArray(or?.data) ? or.data : [];
      } catch {
        try {
          const proms = usersArray.slice(0, 20).map(u => {
            const uid = u.id || u.userId || u.ID;
            return apiService.getUserOrders(uid).catch(() => []);
          });
          allOrders = (await Promise.all(proms)).flat();
        } catch { }
      }

      const totalRevenue = allOrders.reduce((sum, o) =>
        sum + parseFloat(o.subTotal||0) + parseFloat(o.taxAmount||0) +
        parseFloat(o.shippingAmount||0) - parseFloat(o.discountAmount||0), 0);

      const getRaw = (o) => parseInt(o.orderStatus ?? o.status ?? o.OrderStatus ?? 1);
      const pendingOrders  = allOrders.filter(o => getRaw(o) === 1).length;
      const approvedOrders = allOrders.filter(o => getRaw(o) === 2).length;
      const rejectedOrders = allOrders.filter(o => getRaw(o) === 3).length;

      const formattedOrders = allOrders
        .sort((a, b) => new Date(b.orderDate || b.createdDate || 0) - new Date(a.orderDate || a.createdDate || 0))
        .slice(0, 5)
        .map(order => {
          const uid  = order.userId || order.UserId;
          const user = userMap.get(parseInt(uid));
          const total = (
            parseFloat(order.subTotal||0) + parseFloat(order.taxAmount||0) +
            parseFloat(order.shippingAmount||0) - parseFloat(order.discountAmount||0)
          ).toFixed(2);
          const rawStatus = getRaw(order);
          return {
            id:       `#ORD-${String(order.id || order.orderId).padStart(4, '0')}`,
            customer: user?.name || user?.Name || `User #${uid}` || 'Unknown',
            amount:   `$${total}`,
            status:   getOrderStatus(rawStatus),
            statusId: rawStatus,
          };
        });

      const monthlyData = generateMonthlyData(allOrders);
      const orderStatusData = [
        { name: 'Pending',  value: pendingOrders,  color: ORDER_STATUSES[1].color },
        { name: 'Approved', value: approvedOrders, color: ORDER_STATUSES[2].color },
        { name: 'Rejected', value: rejectedOrders, color: ORDER_STATUSES[3].color },
      ].filter(s => s.value > 0);

      setChartData({ revenueChart: monthlyData, ordersChart: monthlyData, orderStatusChart: orderStatusData });
      setDashboardStats({
        totalUsers: usersArray.length, totalOrders: allOrders.length, totalRevenue,
        totalProducts: activeProducts.length, inactiveProducts: inactiveProducts.length,
        allProducts: productsArray.length,
        usersChange: usersArray.length > 0 ? 12 : 0, ordersChange: allOrders.length > 0 ? 8 : 0,
        revenueChange: totalRevenue > 0 ? 23 : 0, productsChange: activeProducts.length > 0 ? 5 : 0,
        pendingOrders, approvedOrders, rejectedOrders,
      });
      setRecentOrders(formattedOrders);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateMonthlyData = (orders) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
    const ms = {};
    months.forEach(m => { ms[m] = { month: m, revenue: 0, orders: 0 }; });
    orders.forEach(o => {
      const m = months[new Date(o.orderDate || o.createdDate).getMonth()];
      if (ms[m]) {
        ms[m].revenue += parseFloat(o.subTotal||0) + parseFloat(o.taxAmount||0) +
          parseFloat(o.shippingAmount||0) - parseFloat(o.discountAmount||0);
        ms[m].orders += 1;
      }
    });
    return months.map(m => ms[m]);
  };

  const handleRefresh = async () => { setRefreshing(true); await fetchDashboardData(); setRefreshing(false); };

  useEffect(() => {
    const h = (e) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(e.target))
        setIsProfileDropdownOpen(false);
    };
    if (isProfileDropdownOpen) {
      document.addEventListener('mousedown', h);
      document.addEventListener('touchstart', h);
    }
    return () => { document.removeEventListener('mousedown', h); document.removeEventListener('touchstart', h); };
  }, [isProfileDropdownOpen]);

const handleLogout = () => {
  const cookiePrefs = localStorage.getItem('ortho_cookie_prefs'); // save it
  localStorage.clear();
  if (cookiePrefs) localStorage.setItem('ortho_cookie_prefs', cookiePrefs); // restore it
  sessionStorage.removeItem(SESSION_KEY);
  alert('Admin logged out successfully');
  onLogout();
  onNavigate('login');
};

  const menuItems = [
    { id: 'dashboard',        icon: LayoutDashboard, label: 'Dashboard'        },
    { id: 'users',            icon: Users,           label: 'Users'            },
    { id: 'orders',           icon: ShoppingBag,     label: 'Orders'           },
    { id: 'active-products',  icon: CheckCircle,     label: 'Active Products'  },
    { id: 'pending-products', icon: Clock,           label: 'Pending Products' },
  ];

  const statsCards = [
    {
      title: 'Total Users',
      value: loading ? '...' : dashboardStats.totalUsers.toLocaleString(),
      change: `+${dashboardStats.usersChange}%`,
      color: '#4361ee',
    },
    {
      title: 'Total Orders',
      value: loading ? '...' : dashboardStats.totalOrders.toLocaleString(),
      subValue: loading ? '' : `${dashboardStats.approvedOrders} Approved · ${dashboardStats.pendingOrders} Pending · ${dashboardStats.rejectedOrders} Rejected`,
      change: `+${dashboardStats.ordersChange}%`,
      color: '#f72585',
    },
    // {
    //   title: 'Revenue',
    //   value: loading ? '...' : `$${dashboardStats.totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    //   change: `+${dashboardStats.revenueChange}%`,
    //   color: '#4cc9f0',
    // },
    // {
    //   title: 'Active Products',
    //   value: loading ? '...' : dashboardStats.totalProducts.toLocaleString(),
    //   change: `+${dashboardStats.productsChange}%`,
    //   color: '#4895ef',
    // },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'orders':           return <OrdersPage />;
      case 'users':            return <UsersPage />;
      case 'active-products':  return <ActiveProductsPage />;
      case 'pending-products': return <PendingProductsPage />;

      case 'dashboard': return (
        <div className="admin-content">
          <div className="content-header">
            <div>
              <h1>Dashboard Overview</h1>
              <p>Welcome back, {adminName}!</p>
            </div>
            <button onClick={handleRefresh} disabled={refreshing}
              style={{ padding: '8px 16px', background: '#018d71', color: 'white', border: 'none', borderRadius: '6px', cursor: refreshing ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '8px', opacity: refreshing ? 0.6 : 1 }}>
              <RefreshCw size={16} className={refreshing ? 'spin' : ''} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>

          <div className="stats-grid">
            {statsCards.map((stat, i) => (
              <div key={i} className="stat-card" style={{ borderLeft: `4px solid ${stat.color}` }}>
                <h3>{stat.title}</h3>
                <div className="stat-value">{stat.value}</div>
                {stat.subValue && (
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3, lineHeight: 1.6 }}>{stat.subValue}</div>
                )}
              </div>
            ))}
          </div>

          <div className="charts-grid">
            {/* Monthly Revenue */}
            {/* <div className="chart-card">
              <h3>Monthly Revenue</h3>
              {loading ? <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Loading chart...</div> : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={chartData.revenueChart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis dataKey="month" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip contentStyle={{ background: '#fff', border: '1px solid #ddd', borderRadius: 8 }} formatter={(v) => [`$${v.toFixed(2)}`, 'Revenue']} />
                    <Legend />
                    <Line type="monotone" dataKey="revenue" stroke="#4cc9f0" strokeWidth={3} dot={{ fill: '#4cc9f0', r: 5 }} activeDot={{ r: 7 }} name="Revenue" />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div> */}

            {/* Monthly Orders */}
            <div className="chart-card">
              <h3>Monthly Orders</h3>
              {loading ? <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Loading chart...</div> : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData.ordersChart}>
                    <CartesianGrid strokeDasharray="3 3" stroke={GREEN_PALETTE.gridStroke} />
                    <XAxis dataKey="month" stroke={GREEN_PALETTE.axisStroke} />
                    <YAxis stroke={GREEN_PALETTE.axisStroke} />
                    <Tooltip contentStyle={{ background: '#fff', border: '1px solid #ddd', borderRadius: 8 }} />
                    <Legend />
                    <Bar dataKey="orders" fill="#4361ee" radius={[8, 8, 0, 0]} name="Orders" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Order Status Distribution */}
            <div className="chart-card">
              <h3>Order Status Distribution</h3>
              <div style={{ display: 'flex', gap: 16, marginBottom: 14, flexWrap: 'wrap' }}>
                {Object.values(ORDER_STATUSES).map(s => (
                  <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                    <div style={{ width: 11, height: 11, borderRadius: '50%', background: s.color }} />
                    <span style={{ color: '#4a5568', fontWeight: 500 }}>{s.label}</span>
                  </div>
                ))}
              </div>
              {loading ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Loading chart...</div>
              ) : chartData.orderStatusChart.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>No orders found</div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={chartData.orderStatusChart}
                      cx="50%" cy="50%" outerRadius={95}
                      dataKey="value"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {chartData.orderStatusChart.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: '#fff', border: '1px solid #ddd', borderRadius: 8 }} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
              {!loading && (
                <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: 14, paddingTop: 14, borderTop: '1px solid #f0f0f0' }}>
                  {[
                    { label: 'Pending',  count: dashboardStats.pendingOrders,  color: ORDER_STATUSES[1].color },
                    { label: 'Approved', count: dashboardStats.approvedOrders, color: ORDER_STATUSES[2].color },
                    { label: 'Rejected', count: dashboardStats.rejectedOrders, color: ORDER_STATUSES[3].color },
                  ].map(s => (
                    <div key={s.label} style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 24, fontWeight: 700, color: s.color }}>{s.count}</div>
                      <div style={{ fontSize: 12, color: '#718096', marginTop: 2 }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="recent-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ margin: 0 }}>Recent Orders</h2>
              <button onClick={() => changeTab('orders')}
                style={{ fontSize: 13, color: '#fff', background: '#018d71', border: 'none', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontWeight: 600 }}>
                View All →
              </button>
            </div>
            {loading ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Loading orders...</div>
            ) : recentOrders.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>No orders found</div>
            ) : (
              <div className="table-container">
                <table className="admin-table">
                  <thead>
                    <tr><th>Order ID</th><th>Customer</th><th>Amount</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    {recentOrders.map((order) => (
                      <tr key={order.id}>
                        <td style={{ fontWeight: 600, color: '#1a1a1a' }}>{order.id}</td>
                        <td>{order.customer}</td>
                        <td>{order.amount}</td>
                        <td>
                          <span style={{
                            display: 'inline-block', padding: '3px 10px', borderRadius: 12,
                            fontSize: 12, fontWeight: 600,
                            background: order.statusId === 2 ? '#dcfce7' : order.statusId === 3 ? '#fee2e2' : '#fef9c3',
                            color:      order.statusId === 2 ? '#166534' : order.statusId === 3 ? '#991b1b' : '#854d0e',
                          }}>
                            {order.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      );

      default: return null;
    }
  };

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="header-left">
          <button className="menu-toggle" onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
            {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          <h2>Admin Panel</h2>
        </div>
        <div className="header-right">
          <div className="admin-profile-dropdown" ref={profileDropdownRef}>
            <button className="admin-profile-btn"
              onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
              aria-label="Profile">
              {/* ✅ First letter of real admin name */}
              <div className="admin-avatar">{adminName.charAt(0).toUpperCase()}</div>
              {/* ✅ Full real admin name */}
              <span className="admin-name">{adminName}</span>
              <ChevronDown size={16} className="dropdown-arrow" />
            </button>
            {isProfileDropdownOpen && (
              <div className="admin-dropdown-menu">
                <button onClick={handleLogout} className="admin-dropdown-item logout-item">
                  <LogOut size={16} /> Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="admin-layout">
        <aside className={`admin-sidebar ${isSidebarOpen ? 'open' : 'closed'}`}>
          <nav className="admin-nav">
            {menuItems.map(({ id, icon: Icon, label }) => (
              <button
                key={id}
                className={`nav-item ${activeTab === id ? 'active' : ''}`}
                onClick={() => changeTab(id)}
              >
                <Icon size={20} /><span>{label}</span>
              </button>
            ))}
          </nav>
        </aside>
        <main className="admin-main">{renderContent()}</main>
      </div>

      <style jsx>{`
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        .spin { animation: spin 1s linear infinite; }
        .content-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:24px; }
        .charts-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(480px,1fr)); gap:24px; margin-bottom:32px; }
        .chart-card { background:white; padding:24px; border-radius:12px; box-shadow:0 2px 8px rgba(0,0,0,0.08); }
        .chart-card h3 { margin:0 0 16px; font-size:18px; font-weight:600; color:#1a1a1a; }
        @media(max-width:1024px){ .charts-grid{grid-template-columns:1fr;} }
      `}</style>
    </div>
  );
};

export default AdminDashboard;